import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';
import { FikoKaynakSembol } from './FikoKaynakSembol';
import * as THREE from 'three';

/**
 * 🔥 CATIA-style Weld Symbol Dialog
 */
@Component({
  selector: 'app-fiko-kaynak-sembol',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './fiko-kaynak-sembol.component.html',
  styleUrls: ['./fiko-kaynak-sembol.component.css']
})
export class FikoKaynakSembolComponent implements OnInit, OnDestroy {
  @Input() anaGosterim: FikoThreeJsComponent;
  @Input() pencere: any;

  // Weld symbol settings
  kaynakTipi: string = 'fillet';
  pozisyon: string = 'arrow';  // arrow, other, both
  boyut: number = 5;  // Weld size (mm)
  uzunluk: number = 0;  // Weld length (0 = continuous)
  adet: number = 0;  // Number of welds
  pitch: number = 0;  // Pitch (spacing)
  konturu: string = 'none';  // none, flat, convex
  tamCevre: boolean = false;  // All-around symbol
  sahadaKaynak: boolean = false;  // Field weld
  renk: string = '#8B4513';
  boyutFont: number = 12;

  // Weld types
  kaynakTipleri = [
    { value: 'fillet', label: 'Köşe Kaynağı (Fillet) ∟' },
    { value: 'groove', label: 'Alın Kaynağı (Groove) V' },
    { value: 'plug', label: 'Tapa Kaynağı (Plug) ○' },
    { value: 'spot', label: 'Nokta Kaynağı (Spot) •' },
    { value: 'seam', label: 'Dikiş Kaynağı (Seam) ═' },
    { value: 'backing', label: 'Destek Kaynağı (Backing) ▭' },
    { value: 'surfacing', label: 'Yüzey Kaynağı ▯' },
    { value: 'edge', label: 'Kenar Kaynağı (Edge) ∪' }
  ];

  // Position options
  pozisyonlar = [
    { value: 'arrow', label: 'Ok Tarafı' },
    { value: 'other', label: 'Diğer Taraf' },
    { value: 'both', label: 'Her İki Taraf' }
  ];

  // Contour options
  konturlar = [
    { value: 'none', label: 'Yok' },
    { value: 'flat', label: 'Düz (─)' },
    { value: 'convex', label: 'Konveks (⌒)' },
    { value: 'concave', label: 'Konkav (⌓)' }
  ];

  // Point selection
  nokta: THREE.Vector3 | null = null;
  noktaSecimModu: boolean = false;

  // Current annotation
  mevcutKaynak: FikoKaynakSembol | null = null;

  // Predefined colors
  renkler = ['#8B4513', '#FF4500', '#FFD700', '#00FFFF', '#FF00FF', '#00FF00'];

  // Sabit boyut - kamera zoom'una göre scale ayarlanır
  sabitBoyut: boolean = true;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Accordion states
  kaynakAyarlariAcik = true;
  olcuAyarlariAcik = false;
  sembolAyarlariAcik = false;
  gorunumAyarlariAcik = false;

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.noktaSecimModu) {
      this.noktaSecimModunuKapat();
    }
  }

  /**
   * Start point selection mode
   */
  noktaSec(): void {
    this.noktaSecimModu = true;
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
      canvas.addEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Handle point click
   */
  private noktaTiklama = (event: MouseEvent) => {
    const position = this.get3DPositionFromMouse(event);
    if (!position) return;

    this.nokta = position;

    this.noktaSecimModunuKapat();
    this.onizlemeGuncelle();
  };

  /**
   * Close point selection mode
   */
  private noktaSecimModunuKapat(): void {
    this.noktaSecimModu = false;
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      canvas.style.cursor = 'default';
      canvas.removeEventListener('click', this.noktaTiklama);
    }
  }

  /**
   * Get 3D position from mouse
   * Uses deep traversal to find all mesh objects
   */
  private get3DPositionFromMouse(event: MouseEvent): THREE.Vector3 | null {
    if (!this.anaGosterim?.sunucu || !this.anaGosterim?.aktifKamera) return null;

    const canvas = this.anaGosterim.sunucu.domElement;
    const rect = canvas.getBoundingClientRect();

    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.anaGosterim.aktifKamera);

    const sahne = this.anaGosterim.sahneler?.[0];
    if (!sahne) return null;

    // Collect ALL meshes recursively (not just direct children)
    const meshes: THREE.Object3D[] = [];
    sahne.traverse((obj: THREE.Object3D) => {
      if ((obj as any).isMesh || obj.type === 'Mesh') {
        meshes.push(obj);
      }
    });

    // Perform raycast against all meshes
    const intersects = raycaster.intersectObjects(meshes, false);

    if (intersects.length > 0) {
      const hit = intersects[0];
      return hit.point.clone();
    }

    // Fallback - project to a plane at z=0

    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    const intersection = new THREE.Vector3();

    if (raycaster.ray.intersectPlane(plane, intersection)) {
      return intersection;
    }

    // Ultimate fallback
    const distance = 5;
    const direction = new THREE.Vector3();
    this.anaGosterim.aktifKamera.getWorldDirection(direction);
    const position = this.anaGosterim.aktifKamera.position.clone();
    position.add(direction.multiplyScalar(distance));

    return position;
  }

  /**
   * Toggle sabit boyut (fixed size regardless of zoom)
   */
  sabitBoyutDegistir(): void {
    if (this.mevcutKaynak) {
      this.mevcutKaynak.setSabitBoyut(this.sabitBoyut);
      this.anaGosterim?.ciz();
    }
  }

  /**
   * Update preview
   */
  onizlemeGuncelle(): void {
    if (!this.nokta) return;

    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne) return;

    // Eski önizleme varsa sil
    if (this.mevcutKaynak) {
      sahne.remove(this.mevcutKaynak);
      this.mevcutKaynak.dispose();
    }

    // Yeni önizleme oluştur
    this.mevcutKaynak = new FikoKaynakSembol(
      sahne,
      this.nokta,
      this.kaynakTipi,
      this.boyut
    );

    sahne.add(this.mevcutKaynak);
    this.anaGosterim?.ciz();
  }

  /**
   * Add weld symbol to scene
   */
  ekle(): void {
    if (!this.nokta) {
      alert('Lütfen önce bir nokta seçin!');
      return;
    }

    const sahne = this.anaGosterim?.sahneler?.[0];
    if (!sahne?.fikoEtiketler) return;

    // Create weld symbol
    const kaynak = new FikoKaynakSembol(
      sahne,
      this.nokta,
      this.kaynakTipi,
      this.boyut
    );

    // Add to etiketler group
    const etiket = sahne.fikoEtiketler.ekleKaynakSembol();
    etiket.userData['FikoAnnotation'] = kaynak;
    kaynak.parent = etiket;
    sahne.add(kaynak);

    this.anaGosterim?.ciz();
    this.anaGosterim?.FikoYT?.degisti?.emit();

    // Reset
    this.nokta = null;
    if (this.mevcutKaynak) {
      sahne.remove(this.mevcutKaynak);
      this.mevcutKaynak = null;
    }
  }

  /**
   * Close dialog
   */
  kapat(): void {
    if (this.mevcutKaynak) {
      const sahne = this.anaGosterim?.sahneler?.[0];
      if (sahne) {
        sahne.remove(this.mevcutKaynak);
        this.mevcutKaynak.dispose();
      }
    }
    // pencere.instance → FikoDialogCore instance
    const dialogInstance = this.pencere?.instance ?? this.pencere;
    if (dialogInstance?.kapa) {
      dialogInstance.kapa();
    } else if (dialogInstance?.kapat) {
      dialogInstance.kapat();
    }
  }
}
